"use client";

import * as React from "react";
import Link from "next/link";
import { useInViewOnce } from "@/utils/masonary/useInViewOnce";
import { fromTransformOf, mapEase } from "@/utils/masonary/anim";
import Image from "next/image";

export type MasonryCoreItem = {
  id: string;
  img: string;
  url?: string;
  height?: number;
  title?: string;
  tags?: string[];
};

export type MasonryItemProps<T extends MasonryCoreItem> = {
  item: T;
  index: number;

  animateFrom: "bottom" | "top" | "left" | "right" | "scale" | "fade";
  ease: string;            // may be token; resolved in component
  duration: number;        // seconds
  stagger: number;         // seconds
  scaleOnHover: boolean;
  hoverScale: number;
  blurToFocus: boolean;
  colorShiftOnHover: boolean;

  renderItem?: (item: T, index: number) => React.ReactNode;
};

function DefaultCard({ item }: { item: MasonryCoreItem }) {
  const { url, img, title, tags, height } = item;

  const card = (
    <figure className="relative overflow-hidden rounded border border-border bg-black">
      {/* image wrapper defines the display height; Image uses fill */}
      <div
        className="relative w-full"
        style={{ height: height ? `${height}px` : undefined }}
      >
        <Image
          src={img}
          alt={title || "Masonry image"}
          fill
          sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
          className="object-cover"
        // uncomment if you add blur data later:
        // placeholder={item.blurDataURL ? "blur" : "empty"}
        // blurDataURL={item.blurDataURL}
        // priority={item.priority} // if you choose to pass it through
        />
      </div>

      {(title || (tags && tags.length)) && (
        <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 from-black/70 via-black/40 to-transparent bg-gradient-to-t px-4 pb-3 pt-10 text-white">
          {title && <p className="text-sm font-medium leading-none">{title}</p>}
          {tags && tags.length > 0 && (
            <p className="mt-1 text-[11px] leading-4 text-white/70">{tags.join(", ")}</p>
          )}
        </figcaption>
      )}
    </figure>
  );

  return url ? (
    <Link
      href={url}
      className="block focus:outline-none"
      aria-label={title || "Masonry item"}
    >
      {card}
    </Link>
  ) : (
    card
  );
}

export default function MasonryItem<T extends MasonryCoreItem>({
  item,
  index,
  animateFrom,
  ease,
  duration,
  stagger,
  scaleOnHover,
  hoverScale,
  blurToFocus,
  colorShiftOnHover,
  renderItem,
}: MasonryItemProps<T>) {
  const { ref, seen } = useInViewOnce<HTMLDivElement>("0px 0px -5% 0px");
  const easing = React.useMemo(() => mapEase(ease), [ease]);
  const fromTransform = React.useMemo(() => fromTransformOf(animateFrom), [animateFrom]);

  // initial → revealed styles
  const revealed = seen;

  return (
    <div
      ref={ref}
      className={[
        "mb-4 md:mb-2 break-inside-avoid",
        "relative will-change-transform will-change-opacity",
        blurToFocus ? "motion-safe:[filter:blur(2px)] focus-within:!blur-0 hover:!blur-0" : "",
        colorShiftOnHover ? "motion-safe:grayscale hover:grayscale-0 focus-within:grayscale-0" : "",
      ].join(" ")}
      style={{
        transform: revealed ? "none" : fromTransform,
        opacity: revealed ? 1 : 0,
        transitionTimingFunction: easing,
        transitionDuration: `${duration}s`,
        transitionDelay: `${(stagger || 0) * index}s`,
      }}
    >
      <div
        className={scaleOnHover ? "group inline-block w-full transition-transform duration-200" : "inline-block w-full"}
        style={scaleOnHover && revealed ? { transform: "none" } : undefined}
      >
        <div
          className={scaleOnHover ? "transition-transform group-hover:[transform]" : undefined}
          style={scaleOnHover ? { transform: `scale(${hoverScale})` } : undefined}
        >
          {renderItem ? renderItem(item, index) : <DefaultCard item={item} />}
        </div>
      </div>
    </div>
  );
}
